//-------------------------------------------------------------------------------------------------- Imports
import {
  Button,
  Dropdown,
  Layout,
  Menu,
  MenuProps,
  Select,
  Skeleton,
  Space,
} from "antd";
import Sider from "antd/es/layout/Sider";
import { Content, Header } from "antd/es/layout/layout";
import {
  MouseEventHandler,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { StyleSheet } from "../StyleSheet";
import { MENU_ITEMS } from "../routes/Routes";
import { FEATURE_RULES } from "../routes/featureRules";
import { images } from "../routes/ImageRoutes";
import { useLocation, useNavigate } from "react-router-dom";
import {
  BellFilled,
  DoubleLeftOutlined,
  DoubleRightOutlined,
  DownOutlined,
  LogoutOutlined,
} from "@ant-design/icons";
import InstallAppModal from "./InstallAppModal";
import { useKeycloak } from "keycloak-react-web";
import Loading from "./Loading";
import { useDispatch, useSelector } from "react-redux";
import { getLoading, setLoading } from "../stores/slices/loadingSlice";
import {
  fillNotificationsSlice,
  getGeneralData,
  setSelectedContract,
} from "../stores/slices/generalDataSlice";
import { AppDispatch } from "../stores/store";
import AlertModal from "./AlertModal";
import { setAlert } from "../stores/slices/alertSlice";
// import BloquedDialog from "./BloquedDialog";
import _ from "lodash";
// import AlertModalImage from "./AlertModalImage";
import { ResponsePopUp } from "../types/common";
import {
  fillMobilePhoneSlice,
  getMobilePhoneData,
} from "../stores/slices/mobilePhoneSlice";
import {
  fillMegaMovilTrackingSlice,
  getTrackingMegamovilData,
} from "../stores/slices/megamovilTrackingSlice";
import { clearUpdatePasswd } from "../stores/slices/updatePasswordSlice";
import {
  fillAssetsConfigSlice,
  getAssetsConfigData,
} from "../stores/slices/assetsConfigSlice";
import { MenuItemType } from "antd/es/menu/hooks/useItems";
import AlertModalReminder from "./AlertModalReminder";
import { checkDebt } from "../stores/slices/checkDebtSlice";
import AlertConfirmModal from "./AlertConfirmModal";
import {
  clearStatusConfirmation,
  setSatusConfirmation,
  Status,
} from "../stores/slices/statusConfirmationSlice";
import ModalConfirmationSlice from "./ModalConfirmationSlice";
import UpdateCurpModal from "./UpdateCurpModal";
import {
  fillDataServiceSlice,
  getConfigurableServiceState,
} from "../stores/slices/configurableServicesSlice";

//-------------------------------------------------------------------------------------------------- Interfaces
interface DefaultLayoutProps {
  children: ReactNode;
}

export interface PaymentData {
  date: string;
  time: string;
  amount?: string;
  name: string;
  client: string;
  card?: string;
  idTransaction?: string;
}

export interface DispatchAlertImageOpts {
  onPressBtnPrimary: Function | undefined;
  onPressBtnSecondary: Function | undefined;
  popupData?: ResponsePopUp;
}

type MenuItem = Required<MenuProps>["items"][number];

//-------------------------------------------------------------------------------------------------- Notifications Bell Component
const NotificationsBell = ({
  unreadCount,
  onClick,
}: {
  unreadCount: number;
  onClick?: MouseEventHandler;
}) => {
  return (
    <div style={stylesBell.mainContainer} onClick={onClick}>
      <BellFilled style={stylesBell.bellStyle} />
      {unreadCount > 0 && (
        <div style={stylesBell.counterDot}>
          {unreadCount > 99 ? "99+" : unreadCount}
        </div>
      )}
    </div>
  );
};

//-------------------------------------------------------------------------------------------------- Default Layout Component
const DefaultLayout = (props: DefaultLayoutProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const generalData = useSelector(getGeneralData);
  const mobileTelephony = useSelector(getMobilePhoneData);
  const megaMovilTracking = useSelector(getTrackingMegamovilData);
  const assetsConfig = useSelector(getAssetsConfigData);
  const confService = useSelector(getConfigurableServiceState);
  const { isLoading } = useSelector(getLoading);
  // Hooks for rendering
  const [hasError, setHasError] = useState<boolean>(false);
  const [collapsed, setCollapsed] = useState<boolean>(false);
  const [collapsedPerSize, setCollapsedPerSize] = useState<boolean>(false);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  // Hooks for userData
  const [clientId, setClientId] = useState<string>("");
  const [contractsItems, setContractsItems] = useState<Array<any>>();
  const navigate = useNavigate();
  const location = useLocation();
  const [items, setItems] = useState<MenuProps["items"]>([]);
  const { keycloak } = useKeycloak();
  const [menuItems, setMenuItems] = useState<MenuItem[]>();

  const handleClickNotificationsBell = useCallback(() => {
    navigate("/notifications");
  }, [navigate]);

  const logoutAction = useCallback(() => {
    dispatch(clearUpdatePasswd());
    keycloak.logout();
  }, [keycloak, dispatch]);

  const navigateTo: MenuProps["onClick"] = (event) => {
    navigate(event.key);
  };

  const setSelectedContractToRedux = useCallback(
    (value: any) => {
      let clientId: string = typeof value === "string" ? value : value.key;

      dispatch(
        setSelectedContract(
          generalData.contracts[
            generalData.contracts.findIndex(
              (obj) => obj.sucursal + obj.suscriptor === clientId,
            )
          ],
        ),
      );
    },
    [dispatch, generalData.contracts],
  );

  // Use effect para validar si se muestra el loading en base a si el estado de la petición al
  // backend es correcto o no.
  useEffect(() => {
    switch (generalData.status) {
      case 0:
        setHasError(false);
        dispatch(setLoading({ isLoading: true }));
        break;
      case 1:
        // Colocamos que no hubo error
        setHasError(false);
        // Seteamos los contratos para mapearlos en el select
        let contractsItems: Array<any> = [];
        let contractsItemsDrop: MenuProps["items"] = [];
        generalData.contracts.forEach((contract) => {
          const clientId =
            contract.sucursal.padStart(3, "0") +
            contract.suscriptor.padStart(7, "0");
          contractsItems.push({
            key: clientId,
            value: clientId,
            label: clientId,
          });
          contractsItemsDrop?.push({
            key: clientId,
            label: `${contract.nombre}`,
            onClick: setSelectedContractToRedux,
          });
        });
        setContractsItems(contractsItems);
        setItems(contractsItemsDrop);
        // Seteamos el contrato seleccionado por defecto en redux.
        dispatch(
          setSelectedContract(
            generalData.contracts[
              generalData.contracts.findIndex(
                (obj) => obj.isAssociatedContract === false,
              )
            ],
          ),
        );
        break;
      default:
        setHasError(true);
        dispatch(setLoading({ isLoading: false }));
        break;
    }
  }, [
    generalData.status,
    generalData.contracts,
    setSelectedContractToRedux,
    dispatch,
  ]);

  useEffect(() => {
    if (generalData.selectedContract !== null) {
      setClientId(
        generalData.selectedContract.sucursal +
          generalData.selectedContract.suscriptor,
      );
      dispatch(setLoading({ isLoading: false }));
    }
  }, [generalData.selectedContract, dispatch]);

  function isMenuItemType(item: any): item is MenuItemType {
    return item && typeof item === "object" && "key" in item;
  }

  const selectedKeys = useMemo(() => {
    const match = menuItems?.find(
      (item) =>
        isMenuItemType(item) &&
        (location.pathname === item.key ||
          location.pathname.startsWith(`${item.key}/`)),
    );
    return match && isMenuItemType(match) ? [String(match.key)] : [];
  }, [location.pathname, menuItems]);

  const getMenus = useCallback(() => {
    let menuItemArray = _.cloneDeep(MENU_ITEMS);
    const stateForRules = {
      configurableService: confService,
      assetsConfig,
      mobilePhone: mobileTelephony,
      megaMovil: megaMovilTracking,
    };
    Object.values(FEATURE_RULES).forEach((rule) => {
      if (!rule.menuKeys || rule.menuKeys.length === 0) return;
      if (!rule.isAllowed(rule.select(stateForRules))) {
        menuItemArray = menuItemArray.filter(
          (menuItem) =>
            !(isMenuItemType(menuItem) && rule.menuKeys!.includes(menuItem.key as string)),
        );
      }
    });
    setMenuItems(menuItemArray);
  }, [
    confService,
    assetsConfig,
    mobileTelephony,
    megaMovilTracking,
  ]);

  useEffect(() => {
    if (
      !_.isEmpty(assetsConfig.clientId) &&
      !_.isEmpty(mobileTelephony.clientId) &&
      assetsConfig.clientId === mobileTelephony.clientId
    ) {
      getMenus();
    }
  }, [assetsConfig, mobileTelephony, getMenus]);

  useEffect(() => {
    const handleWindowResize = () => {
      setScreenWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleWindowResize);

    setCollapsedPerSize(screenWidth < 960);

    return () => {
      window.removeEventListener("resize", handleWindowResize);
    };
  }, [screenWidth]);

  useEffect(() => {
    setCollapsed(collapsedPerSize);
  }, [collapsedPerSize]);

  useEffect(() => {
    dispatch(fillDataServiceSlice());
    if (clientId) {
      dispatch(fillNotificationsSlice({ clientId: clientId }));
      dispatch(fillMobilePhoneSlice({ clientId: clientId }));
      dispatch(fillAssetsConfigSlice({ clientId: clientId }));
      dispatch(fillMegaMovilTrackingSlice({ clientId: clientId }));
      dispatch(checkDebt({ clientId: clientId }));
    }
  }, [clientId, dispatch]);

  useEffect(() => {
    if (
      clientId &&
      megaMovilTracking.clientId === clientId &&
      megaMovilTracking.status
    ) {
      dispatch(
        setSatusConfirmation({
          textAnts: "Estimado Usuario",
          subtitle: `Le informamos el estado de su contratación realizada el día`,
          date: megaMovilTracking.fecha,
          text: "de su Mega móvil",
          plan: megaMovilTracking.paquete,
          state: megaMovilTracking.estadoContratacion,
          visible: true,
          status: Status.SUCCESS,
        }),
      );
    } else {
      dispatch(clearStatusConfirmation());
    }
  }, [clientId, megaMovilTracking, dispatch]);

  useEffect(() => {
    if (
      generalData.selectedContract &&
      generalData.selectedContract.notifications
    ) {
      let notifications = generalData.selectedContract.notifications;
      let unreadCount = notifications.filter((ntf) => !ntf.read).length;

      setUnreadCount(unreadCount);
    }
  }, [
    generalData.selectedContract,
    generalData.selectedContract?.notifications,
    setUnreadCount,
  ]);

  useEffect(() => {
    setCollapsed(collapsedPerSize);
  }, [collapsedPerSize]);

  useEffect(() => {
    dispatch(
      setAlert({
        show: hasError,
        type: "ERROR",
        title: "¡Estimado usuario!",
        message:
          "Ocurrió un error al momento de obtener la información necesaria para el uso de la aplicación.\nInténtelo de nuevo más tarde.",
        onFinalize: logoutAction,
        // Ejemplo de como pasar botones extras:
        //extraComponents: <Button onClick={() => {dispatch(showAlert(false)); dispatch(clearAlert); setHasError(false)}}>El pepe</Button>
      }),
    );
  }, [hasError, dispatch, logoutAction]);

  return (
    <Layout style={{ height: "100vh", width: "100%" }}>
      <Sider style={styles.siderContainer} width={300} collapsed={collapsed}>
        <div style={styles.mainInfoContainer}>
          {!collapsed && (
            <Skeleton style={styles.skeletonStyles} loading={isLoading}>
              <div style={styles.infoContainer}>
                <h3
                  style={{ fontWeight: 500 }}
                >{`${generalData.selectedContract?.nombre} ${generalData.selectedContract?.apellidoPaterno} ${generalData.selectedContract?.apellidoMaterno}`}</h3>
                <h5 style={{ fontWeight: 400 }}>Suscriptor: {clientId}</h5>
                <h5>{generalData.selectedContract?.paquete}</h5>
                <Select
                  style={styles.selectContractSider}
                  placeholder="Selecciona tu Contrato"
                  optionFilterProp="children"
                  onChange={(value) => setSelectedContractToRedux(value)}
                  value={clientId}
                  options={contractsItems}
                />
              </div>
            </Skeleton>
          )}
        </div>
        <Menu
          style={{ marginTop: collapsed ? 70 : 0 }}
          mode="inline"
          theme="light"
          inlineCollapsed={collapsed}
          items={menuItems}
          selectedKeys={selectedKeys}
          onClick={navigateTo}
        />
      </Sider>
      <Layout>
        <Header style={styles.headerContainer}>
          <div
            style={styles.imageContainer}
            onClick={() => setCollapsed(!collapsed)}
          >
            <div style={styles.iconContainer}>
              {collapsed ? (
                <div
                  style={{ width: 80, display: "flex", flexDirection: "row" }}
                >
                  <div
                    style={{
                      width: "70%",
                      display: "flex",
                      justifyContent: "flex-end",
                    }}
                  >
                    <img
                      style={styles.logoMegacable}
                      src={images.logoMega1}
                      alt="logo megacable"
                    />
                  </div>
                  <div style={{ width: "30%" }}>
                    <div style={styles.arrowsContainer}>
                      {collapsed ? (
                        <DoubleRightOutlined style={{ color: "#ffffff" }} />
                      ) : (
                        <DoubleLeftOutlined style={{ color: "#ffffff" }} />
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div
                  style={{ width: 300, display: "flex", flexDirection: "row" }}
                >
                  <div
                    style={{
                      width: "90%",
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    <img
                      style={styles.logoMegacable}
                      src={images.logoMega2}
                      alt="logo megacable"
                    />
                  </div>
                  <div style={{ width: "10%" }}>
                    <div style={styles.arrowsContainer}>
                      {collapsed ? (
                        <DoubleRightOutlined style={{ color: "#ffffff" }} />
                      ) : (
                        <DoubleLeftOutlined style={{ color: "#ffffff" }} />
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div style={styles.selectContainer}>
            <div style={{ cursor: "pointer" }}>
              {!isLoading && (
                <Dropdown menu={{ items }} trigger={["click"]}>
                  <p onClick={(e) => e.preventDefault()}>
                    <Space style={{ color: "#ffffff" }}>
                      {generalData.selectedContract?.nombre} <DownOutlined />
                    </Space>
                  </p>
                </Dropdown>
              )}
            </div>
          </div>
          <div
            style={{
              ...styles.bellContainer,
              width: "10%",
              justifyContent: "flex-end",
            }}
          >
            <NotificationsBell
              unreadCount={unreadCount}
              onClick={handleClickNotificationsBell}
            />
          </div>
          <div
            style={{
              ...styles.bellContainer,
              width: "25%",
              whiteSpace: "nowrap",
            }}
          >
            <Button
              shape="default"
              type="primary"
              style={{ backgroundColor: "#00339e", width: 35, height: 35 }}
              icon={<LogoutOutlined />}
              onClick={logoutAction}
            />
            <span
              style={{
                cursor: "pointer",
                color: "#fff",
                fontWeight: 600,
                marginLeft: 10,
              }}
              onClick={logoutAction}
            >
              Cerrar sesión
            </span>
          </div>
        </Header>
        <Content
          style={{ ...styles.mainContainer, marginLeft: !collapsed ? 300 : 80 }}
        >
          {isLoading ? <Loading /> : props.children}
        </Content>
      </Layout>
      <InstallAppModal show={screenWidth < 768} />
      <AlertModal />
      <AlertModalReminder />
      <UpdateCurpModal />
      <AlertConfirmModal />
      {/* <AlertModalImage /> */}
      <ModalConfirmationSlice />
      {/* <BloquedDialog /> */}
    </Layout>
  );
};

//-------------------------------------------------------------------------------------------------- Styles
// Bell
const stylesBell = StyleSheet.create({
  mainContainer: {
    display: "flex",
    width: 40,
    height: 40,
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
  },
  bellStyle: {
    fontSize: 22,
    color: "#ffffff",
  },
  counterDot: {
    display: "flex",
    position: "absolute",
    minWidth: 15,
    height: 15,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "50%",
    backgroundColor: "#ff0000",
    translate: "10px -10px",
    boxShadow: "1px 1px 4px #000000",
    color: "white",
    fontSize: 10,
    fontWeight: "bold",
    padding: "0 4px", // Adjusted padding for a more circular appearance
  },
});

// Component
const styles = StyleSheet.create({
  headerContainer: {
    display: "flex",
    width: "100%",
    height: 70,
    backgroundColor: "#0055ba",
    margin: 0,
    padding: 0,
    zIndex: 1,
  },
  selectContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    width: "50%",
  },
  bellContainer: {
    display: "flex",
    width: "10%",
    height: "100%",
    alignItems: "center",
    justifyContent: "center",
  },
  logoMegacable: {
    height: 30,
  },
  siderContainer: {
    backgroundColor: "#ffffff",
    boxShadow: "0px 0px 20px #e8e8e8",
    zIndex: 0,
    position: "fixed",
    height: "100vh",
    overflowY: "auto",
  },
  imageContainer: {
    display: "flex",
    width: "100%",
    height: 70,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#0055ba",
    cursor: "pointer",
  },
  iconContainer: {
    flex: 1,
    display: "flex",
  },
  arrowsContainer: {
    display: "flex",
    height: "100%",
    alignItems: "center",
    justifyContent: "flex-end",
  },
  mainInfoContainer: {
    display: "flex",
    width: "100%",
    justifyContent: "center",
  },
  skeletonStyles: {
    width: "90%",
  },
  infoContainer: {
    width: "90%",
    padding: "100px 0px 60px 0px",
    flexDirection: "column",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
  },
  selectContractSider: {
    width: "80%",
  },
  mainContainer: {
    backgroundColor: "#f2f2f2",
    overflow: "auto",
  },
});

//-------------------------------------------------------------------------------------------------- Exports
export default DefaultLayout;
